import React, { createContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { plainToInstance } from 'class-transformer';
import getCompanyDetailInfo from '../api/CompanyDetailInfo';
import CompanyInfo from './model/CompanyInfo';
import { Period } from './types/interfaces';

// 회사 정보를 위한 Context 생성 (타입 적용)
export const CompanyContext = createContext<CompanyInfo>(new CompanyInfo());

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ParamsProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(new CompanyInfo());
  const [previousPageCode, setPreviousPageCode] = useState<string | null>(null);

  // 날짜 포맷팅 함수를 밖으로 빼서 재사용
  const formatDateString = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) return '';
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  };

  // 현재 페이지의 코드를 추출하는 함수 (OGAI + 4자리 숫자)
  const getCurrentPageCode = (): string | null => {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/OGAI(\d{4})/);
    return match ? `OGAI${match[1]}` : null;
  };

  // UUID 유효성 검사 함수
  const isValidUUID = (uuid: string | null): boolean => {
    if (!uuid) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const fetchCompanyInfo = async (cId: string): Promise<void> => {
    try {
      const companyDetailInfo = await getCompanyDetailInfo(cId);
      const newCompanyInfo = plainToInstance(CompanyInfo, companyDetailInfo.data);
      
      // updateAccountList 함수 추가
      newCompanyInfo.updateAccountList = (updater) => {
        setCompanyInfo(prev => {
          const updated = new CompanyInfo();
          Object.assign(updated, prev);
          updated.accountList = updater(prev.accountList || []);
          return updated;
        });
      };
      
      // 기수 목록은 period 숫자 오름차순 정렬
      newCompanyInfo?.periodList?.sort((a: Period, b: Period) => a.period - b.period);
      setCompanyInfo(newCompanyInfo);

      if (companyDetailInfo.accountingPeriod) {
        const newParams = new URLSearchParams(searchParams);
        const { period, startDate, endDate } = companyDetailInfo.data.accountingPeriod;

        // period가 없을 경우에만 설정
        if (!searchParams.get('period')) {
          newParams.set('period', period.toString());
        }

        // dateRange가 없을 경우에만 설정
        if (!searchParams.get('dateRange') || searchParams.get('dateRange')?.length !== 16) {
          newParams.set('dateRange',
            `${startDate}${endDate}`.replace(/-/g, '')
          );
        }

        // cId가 없을 경우에만 설정
        if (!newParams.get('cId')) {
          newParams.set('cId', cId);
        }

        setSearchParams(newParams, { replace: true });
      }
    } catch (error) {
      console.error('회사 정보 조회 실패:', error);
      // alert('회사 정보 조회 실패');
    }
  };
  
  useEffect(() => {
    const cId = searchParams.get('cId');
    const period = searchParams.get('period');
    const dateRange = searchParams.get('dateRange');

    // 날짜 정보 저장
    sessionStorage.setItem('startDate', formatDateString(dateRange?.substring(0, 8) || ''));
    sessionStorage.setItem('endDate', formatDateString(dateRange?.substring(8, 16) || ''));

    // cId 검증
    if (!isValidUUID(cId) || cId === null || cId === "null" || cId === undefined) {
      window.location.href = 'https://www.xadom.ai/';
      return;
    }

    // 현재 페이지 코드 확인
    const currentPageCode = getCurrentPageCode();
    
    // 이전 페이지 코드와 현재 페이지 코드가 같다면 회사 정보 호출 건너뛰기
    if (currentPageCode && previousPageCode === currentPageCode) {
      console.log(`페이지 코드가 동일함 (${currentPageCode}), 회사 정보 호출 건너뛰기`);
      return;
    }

    // 페이지 코드가 다르거나 처음 호출인 경우 회사 정보 조회
    if (currentPageCode) {
      setPreviousPageCode(currentPageCode);
      fetchCompanyInfo(cId);
      console.log(`페이지 코드 변경: ${previousPageCode} -> ${currentPageCode}, 회사 정보 호출`);
    } else {
      // 페이지 코드가 없는 경우에도 호출 (기존 동작 유지)
      fetchCompanyInfo(cId);
    }

  }, [searchParams, previousPageCode]); // previousPageCode 의존성 추가

  // Context Provider로 children을 감싸서 companyInfo를 전달
  return (
    <CompanyContext.Provider value={companyInfo}>
      {children}
    </CompanyContext.Provider>
  );
};

export default ParamsProvider;